import React, {
  useState, useContext, useRef, useEffect, useMemo,
} from 'react';
import {
  Box, Button, Alert, Paper, Typography, Stack, Divider, Chip, IconButton, Tooltip, Dialog,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import AppContext from '../context';
import logger from '../utils/logger';
import APIWrapper from '../utils/graphqlwrapper';
import { replaceEntry } from '../utils';
import UploadAudiencesModal from '../components/client/UploadAudiencesModal';

function ClientAudiences(props) {
  const { client } = props;
  const [newAbbreviation, setNewAbbreviation] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [error, setError] = useState('');
  const [audiences, setAudiences] = useState({});
  const [editingAbbreviationOriginal, setEditingAbbreviationOriginal] = useState('');
  const [editingAbbreviation, setEditingAbbreviation] = useState('');
  const [editingFullName, setEditingFullName] = useState('');
  const [editError, setEditError] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [open, setOpen] = useState(false);
  const [audienceToDelete, setAudienceToDelete] = useState('');

  const { setShouldLogOut, authenticated } = useContext(AppContext);
  const hasFetchedData = useRef(false);
  // to track if there was any data returned from the API initially
  const hasDataInitially = useRef(false);

  const helperText = useMemo(
    () => `${Object.keys(audiences).length} scoped to ${client.toUpperCase()}`,
    [audiences, client],
  );

  useEffect(() => {
    let audiencesToSet = {};
    const fetchAudiences = async () => {
      const fetchedValues = await APIWrapper.queryApi(
        { query: 'clientEnumsByClientID' },
        setShouldLogOut,
        {
          client_id: client,
        },
      );

      const dataExists = fetchedValues?.data?.[0]?.[0];
      if (dataExists?.audience_values) {
        try {
          audiencesToSet = JSON.parse(dataExists.audience_values || '{}');
        } catch (e) {
          logger.error('Error parsing audience_values JSON:', e);
          audiencesToSet = {};
        }
      }
      setAudiences(audiencesToSet);
      // to track whether we need to create a new row in the table or update
      hasDataInitially.current = Boolean(dataExists);
    };

    if (!hasFetchedData.current && authenticated) {
      hasFetchedData.current = true;
      fetchAudiences()
        .catch((e) => {
          hasFetchedData.current = false;
          logger.error(e);
        });
    }
  }, [authenticated]);

  const createClientAudiences = async (newValues) => {
    try {
      await APIWrapper.queryApi(
        { mutation: 'createClientEnums' },
        setShouldLogOut,
        {
          input: {
            client_id: client,
            audience_values: JSON.stringify(newValues),
          },
        },
      );
    } catch (e) {
      logger.error(e);
    }
  };

  const updateClientAudiences = async (newValues) => {
    try {
      await APIWrapper.queryApi(
        { mutation: 'updateClientEnums' },
        setShouldLogOut,
        {
          input: {
            client_id: client,
            audience_values: JSON.stringify(newValues),
          },
        },
      );
    } catch (e) {
      logger.error(e);
    }
  };

  const handleAdd = async () => {
    const audiencesCopy = { ...audiences };
    // normalize the new code and label by trimming whitespace and lowering the code
    const normalizedCode = newAbbreviation.trim().toLowerCase();
    const normalizedLabel = newFullName.trim();
    if (!normalizedCode) {
      setError('Abbreviation cannot be empty.');
      return;
    }
    if (!normalizedLabel) {
      setError('Full name cannot be empty.');
      return;
    }
    // spaces/underscores are rejected rather than silently stripped
    if (/[\s_]/.test(normalizedCode)) {
      setError('Codes cannot contain spaces or underscores.');
      return;
    }
    // check if the new code already exists in the values object
    if (audiences[normalizedCode]) {
      setError(`The abbreviation "${normalizedCode}" already exists. Please choose a different abbreviation. Or update the existing full name by clicking the edit icon next to it.`);
      return;
    }
    audiencesCopy[normalizedCode] = normalizedLabel;
    setAudiences(audiencesCopy);
    setNewAbbreviation('');
    setNewFullName('');
    setError('');
    if (hasDataInitially.current) {
      updateClientAudiences(audiencesCopy);
    } else {
      createClientAudiences(audiencesCopy).then(() => { hasDataInitially.current = true; });
    }
  };

  const handleDeleteClick = (audience) => {
    setOpen(true);
    setConfirmationPopup(true);
    setAudienceToDelete(audience);
  };

  const handleRemove = async () => {
    const audiencesCopy = { ...audiences };
    delete audiencesCopy[audienceToDelete];
    setOpen(false);
    setConfirmationPopup(false);
    setAudienceToDelete('');
    setError('');
    setAudiences(audiencesCopy);
    updateClientAudiences(audiencesCopy);
  };

  const startEdit = (edit) => {
    setEditingAbbreviationOriginal(edit);
    setEditingAbbreviation(edit);
    setEditingFullName(audiences[edit]);
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingAbbreviationOriginal('');
    setEditingAbbreviation('');
    setEditingFullName('');
    setEditError('');
  };

  const commitEdit = async () => {
    // code is left empty, show error and don't commit
    if (!editingAbbreviation) {
      setEditError('Abbreviation cannot be empty.');
      return;
    }
    // underscores and spaces in audience code are rejected rather than silently stripped
    if (/[\s_]/.test(editingAbbreviation)) {
      setEditError('Abbreviations cannot contain spaces or underscores.');
      return;
    }
    // label is left empty, show error and don't commit
    if (!editingFullName) {
      setEditError('Full name cannot be empty.');
      return;
    }
    // new code is different from the original and already exists in the values object, return
    if (editingAbbreviation !== editingAbbreviationOriginal && audiences[editingAbbreviation]) {
      setEditError(`The abbreviation "${editingAbbreviation}" already exists. Please choose a different abbreviation.`);
      return;
    }
    const newAudiences = replaceEntry(
      audiences,
      editingAbbreviationOriginal,
      editingAbbreviation,
      editingFullName,
    );
    setAudiences(newAudiences);
    updateClientAudiences(newAudiences);
    // clear the editing state after saving
    cancelEdit();
  };

  return (
    <Box
      sx={{ mb: 1, maxWidth: 700 }}
    >
      <Alert sx={{ mb: 2 }} severity="info" icon={<GroupsOutlinedIcon fontSize="small" />}>
        {`These values populate the Audience column when grouping ${client.toUpperCase()}'s codes. They're specific to this client — adding one here doesn't affect other clients' audience lists.`}
      </Alert>
      <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'common.white' }}>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5,
        }}
        >
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            Add a new audience
          </Typography>
          <Button size="small" onClick={() => setUploadModalOpen(true)}>
            Upload Audiences from CSV
          </Button>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <TextField
            size="small"
            label="Abbreviation"
            placeholder="e.g. mid-dollar"
            value={newAbbreviation}
            onChange={(e) => setNewAbbreviation(e.target.value)}
            sx={{ width: 220 }}
          />
          <TextField
            size="small"
            label="Full Name"
            placeholder="e.g. Mid-Dollar Donors"
            value={newFullName}
            onChange={(e) => setNewFullName(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{ mt: 0.25 }}
          >
            Add
          </Button>
        </Stack>
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {error}
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ bgcolor: 'common.white' }}>
        <Box sx={{
          px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between',
        }}
        >
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            Current audiences
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {helperText}
          </Typography>
        </Box>
        <Divider />
        <Stack divider={<Divider />}>
          {Object.keys(audiences).length === 0 && (
          <Typography variant="body2" sx={{ px: 2.5, py: 3, color: 'text.secondary' }}>
            {`No audience values yet for ${client.toUpperCase()}.`}
          </Typography>
          )}
          {Object.keys(audiences).map((key) => {
            const current = key;
            const isEditing = editingAbbreviationOriginal === current;
            return (
              <Box
                key={current}
                sx={{
                  px: 2.5,
                  py: 1.25,
                  '&:hover': { bgcolor: isEditing ? undefined : 'datagrid.selectedOrHover' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {isEditing ? (
                    <>
                      <TextField
                        size="small"
                        value={editingAbbreviation}
                        onChange={(e) => setEditingAbbreviation(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && commitEdit()}
                        autoFocus
                        sx={{ width: 220, '& input': { fontFamily: 'monospace', fontSize: 13 } }}
                      />
                      <TextField
                        size="small"
                        value={editingFullName}
                        onChange={(e) => setEditingFullName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && commitEdit()}
                        sx={{ flex: 1 }}
                      />
                      <Tooltip title="Save">
                        <IconButton size="small" onClick={commitEdit}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={cancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Chip
                        label={current}
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          bgcolor: 'datagrid.headerBg',
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {audiences[current]}
                      </Typography>
                      <Tooltip title="Edit abbreviation or full name">
                        <IconButton size="small" onClick={() => startEdit(current)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove audience">
                        <IconButton size="small" onClick={() => handleDeleteClick(current)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
                {isEditing && editError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {editError}
                </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Paper>

      {
        confirmationPopup && (
        <Dialog open={open} maxWidth="sm" fullWidth>
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 3, padding: 2, justifyContent: 'center', margin: 'auto',
          }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'rgb(72, 101, 129)',
                fontWeight: 600,
                fontSize: '1.2rem',
                lineHeight: 1.4,
              }}
            >
              Are you sure you want to delete the following audience?
            </Typography>
            <div style={{ display: 'flex', gap: '1em' }}>
              <Chip
                label={audienceToDelete}
                size="small"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  bgcolor: 'datagrid.headerBg',
                  fontWeight: 600,
                }}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {audiences[audienceToDelete]}
              </Typography>
            </div>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => setOpen(false)} sx={{ width: '45%', fontWeight: 600 }}>No</Button>
              <Button variant="contained" onClick={handleRemove} sx={{ width: '45%', fontWeight: 600 }}>Yes</Button>
            </Box>
          </Box>
        </Dialog>
        )
              }

      <UploadAudiencesModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        existingAudiences={audiences}
        onConfirm={(mergedValues) => {
          setAudiences(mergedValues);
          if (hasDataInitially.current) {
            updateClientAudiences(mergedValues);
          } else {
            createClientAudiences(mergedValues);
          }
        }}
      />
    </Box>
  );
}

export default ClientAudiences;

ClientAudiences.propTypes = {
  client: PropTypes.string,
};
